import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { firstValueFrom, Subject, takeUntil } from 'rxjs'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { AppStateService } from 'src/app/core/services/app-state.service'
import {
  ConstructionSitesLineProperties,
  ConstructionSitesProperties,
} from '../models/construction-sites.model'
import { FeatureCollection } from 'geojson'

@Injectable()
export class DataConstructionSitesService {
  private constructionSites: FeatureCollection<
    GeoJSON.Point | GeoJSON.LineString,
    ConstructionSitesProperties | ConstructionSitesLineProperties
  >[] = [
    {
      type: 'FeatureCollection',
      features: [],
    },
    {
      type: 'FeatureCollection',
      features: [],
    },
  ]
  public isDataInitialized = false

  // Add request cancellation mechanism
  private cancelPreviousRequest$ = new Subject<void>()

  constructor(
    private readonly http: HttpClient,
    private appState: AppStateService,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {}

  async loadConstructionSitesData(
    isHistoryEnabled: boolean,
    bounds: maplibregl.LngLatBounds,
    timestamp?: Date,
  ) {
    // Cancel any previous ongoing request
    this.cancelPreviousRequest$.next()

    this.isDataInitialized = true

    const constructionSites =
      isHistoryEnabled && timestamp
        ? await this.loadConstructionSitesForHistory(timestamp, bounds)
        : await this.loadConstructionSites(bounds)

    this.constructionSites[0].features = constructionSites.features

    this.constructionSites[1].features = constructionSites.features
      .map((feature) => {
        const lineStringFeature = feature.properties.lineStringFeature
        if (!lineStringFeature) {
          return null
        }

        return {
          ...lineStringFeature,
          id: feature.properties.id,
          properties: {
            id: feature.properties.id,
            direction: feature.properties.direction,
          },
        } as GeoJSON.Feature<GeoJSON.LineString, ConstructionSitesLineProperties>
      })
      .filter(
        (
          feature,
        ): feature is GeoJSON.Feature<GeoJSON.LineString, ConstructionSitesLineProperties> =>
          feature !== null,
      )

    return this
  }

  async loadConstructionSites(
    bounds: maplibregl.LngLatBounds,
  ): Promise<FeatureCollection<GeoJSON.Point, ConstructionSitesProperties>> {
    const httpOptions = {
      headers: new HttpHeaders({
        accept: 'json',
      }),
    }
    const url = new URL(`${this.config.backend_url}vzo/construction-sites`)
    url.searchParams.set('minLng', bounds.getWest().toString())
    url.searchParams.set('minLat', bounds.getSouth().toString())
    url.searchParams.set('maxLng', bounds.getEast().toString())
    url.searchParams.set('maxLat', bounds.getNorth().toString())

    // Add takeUntil to cancel the request when a new one starts
    const result = await firstValueFrom(
      this.http
        .get<FeatureCollection<GeoJSON.Point, ConstructionSitesProperties>>(
          url.toString(),
          httpOptions,
        )
        .pipe(takeUntil(this.cancelPreviousRequest$)),
    )
    return result
  }

  async loadConstructionSitesForHistory(
    timestamp: Date,
    bounds: maplibregl.LngLatBounds,
  ): Promise<FeatureCollection<GeoJSON.Point, ConstructionSitesProperties>> {
    const httpOptions = {
      headers: new HttpHeaders({
        accept: 'json',
      }),
      params: {
        timestamp: timestamp.toISOString(),
        minLat: bounds.getSouth().toString(),
        maxLat: bounds.getNorth().toString(),
        minLng: bounds.getWest().toString(),
        maxLng: bounds.getEast().toString(),
      },
    }

    const url = new URL(`${this.config.backend_url}vzo/construction-sites/historic`)

    const result = await firstValueFrom(
      this.http
        .get<FeatureCollection<GeoJSON.Point, ConstructionSitesProperties>>(
          url.toString(),
          httpOptions,
        )
        .pipe(takeUntil(this.cancelPreviousRequest$)),
    )

    return result
  }

  getConstructionSites(): FeatureCollection<
    GeoJSON.Point | GeoJSON.LineString,
    ConstructionSitesProperties | ConstructionSitesLineProperties
  >[] {
    return this.constructionSites
  }

  // Method to manually cancel current requests (if needed by external services)
  public cancelCurrentRequests(): void {
    this.cancelPreviousRequest$.next()
  }

  // Clean up on service destruction
  ngOnDestroy(): void {
    this.cancelPreviousRequest$.next()
    this.cancelPreviousRequest$.complete()
  }
}
